/**
 * Painel do fabricante — Gestão de Empresas (ADR 0006).
 *
 * Tela do DONO DO PRODUTO (não da operadora): cadastro de empresas
 * prospect/cliente e criação do admin inicial com senha provisória.
 * Só aparece para usuários com papel fabricante_admin em instância
 * com AURA_FABRICANTE=1.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Briefcase, Building2, Copy, KeyRound, Pencil, Plus, UserPlus, X,
} from 'lucide-react';
import fabricanteAPI from '../../services/fabricanteAPI';

const STATUS_INFO = {
  prospect: { label: 'Prospect', cor: '#3498db' },
  demo: { label: 'Demo liberada', cor: '#f39c12' },
  ativo: { label: 'Cliente ativo', cor: '#27ae60' },
  suspenso: { label: 'Suspenso', cor: '#e74c3c' },
};

const EMPRESA_VAZIA = {
  cnpj: '', razao_social: '', nome_fantasia: '',
  contato_nome: '', contato_email: '', telefone: '',
  status: 'prospect', observacoes: '',
};

const formatarCnpj = (cnpj) => {
  const d = (cnpj || '').replace(/\D/g, '');
  if (d.length !== 14) return cnpj;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
};

const GestaoEmpresas = () => {
  const [empresas, setEmpresas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);

  // modal empresa (criar/editar)
  const [modalEmpresa, setModalEmpresa] = useState(null); // {modo:'criar'|'editar', dados, id}
  // modal admin
  const [modalAdmin, setModalAdmin] = useState(null); // {empresa, nome, email, criado}
  const [salvando, setSalvando] = useState(false);
  const [erroModal, setErroModal] = useState(null);
  const [copiado, setCopiado] = useState(false);

  const carregar = useCallback(() => {
    setCarregando(true);
    fabricanteAPI.listarEmpresas()
      .then((data) => { setEmpresas(data.empresas || []); setErro(null); })
      .catch((e) => setErro(e?.response?.data?.detail || 'Erro ao carregar empresas'))
      .finally(() => setCarregando(false));
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const salvarEmpresa = () => {
    if (salvando) return;
    setSalvando(true);
    setErroModal(null);
    const { modo, dados, id } = modalEmpresa;
    const corpo = { ...dados };
    Object.keys(corpo).forEach((k) => { if (corpo[k] === '') corpo[k] = null; });
    const req = modo === 'criar'
      ? fabricanteAPI.criarEmpresa({ ...corpo, cnpj: dados.cnpj, razao_social: dados.razao_social, status: dados.status })
      : fabricanteAPI.atualizarEmpresa(id, (({ cnpj, ...resto }) => resto)(corpo));
    req.then(() => { setModalEmpresa(null); carregar(); })
      .catch((e) => {
        const det = e?.response?.data?.detail;
        setErroModal(typeof det === 'string' ? det : 'Dados inválidos. Confira CNPJ (14 dígitos) e razão social');
      })
      .finally(() => setSalvando(false));
  };

  const criarAdmin = () => {
    if (salvando) return;
    setSalvando(true);
    setErroModal(null);
    fabricanteAPI.criarAdmin(modalAdmin.empresa.id, {
      nome: modalAdmin.nome, email: modalAdmin.email,
    })
      .then((criado) => { setModalAdmin((m) => ({ ...m, criado })); carregar(); })
      .catch((e) => {
        const det = e?.response?.data?.detail;
        setErroModal(typeof det === 'string' ? det : 'Erro ao criar acesso');
      })
      .finally(() => setSalvando(false));
  };

  const copiarCredenciais = () => {
    const c = modalAdmin.criado;
    navigator.clipboard.writeText(
      `Acesso AURA (demo)\nUsuário: ${c.email}\nSenha provisória: ${c.senha_provisoria}\nTroque a senha no primeiro acesso.`
    ).then(() => { setCopiado(true); setTimeout(() => setCopiado(false), 2000); });
  };

  const inputStyle = {
    width: '100%', padding: '8px 10px', border: '1px solid var(--gray-200)',
    borderRadius: 6, fontSize: '0.9rem', boxSizing: 'border-box',
  };
  const labelStyle = { fontSize: '0.8rem', fontWeight: 600, color: 'var(--gray-500)', display: 'block', marginBottom: 4 };

  const Campo = ({ label, children }) => (
    <div style={{ marginBottom: 12 }}>
      <span style={labelStyle}>{label}</span>
      {children}
    </div>
  );

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Briefcase size={26} color="var(--brand, #20a490)" />
          <div>
            <h1 style={{ margin: 0, fontSize: '1.4rem' }}>Gestão de Empresas</h1>
            <p style={{ margin: 0, color: 'var(--gray-500)', fontSize: '0.85rem' }}>
              Painel do fabricante: clientes, prospects e acessos de demonstração
            </p>
          </div>
        </div>
        <button
          onClick={() => { setErroModal(null); setModalEmpresa({ modo: 'criar', dados: { ...EMPRESA_VAZIA } }); }}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, background: 'var(--brand, #20a490)',
            color: 'var(--brand-ink, #fff)', border: 'none', borderRadius: 8, padding: '10px 16px',
            fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem',
          }}
        >
          <Plus size={16} /> Nova empresa
        </button>
      </div>

      {/* Resumo por status */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
        {Object.entries(STATUS_INFO).map(([chave, info]) => (
          <div key={chave} style={{
            background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 10,
            padding: '12px 16px', borderLeft: `4px solid ${info.cor}`,
          }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>
              {empresas.filter((e) => e.status === chave).length}
            </div>
            <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)' }}>{info.label}</div>
          </div>
        ))}
      </div>

      {erro && (
        <div style={{ background: '#fdecea', color: '#e74c3c', padding: 12, borderRadius: 8, marginBottom: 16 }}>
          {erro}
        </div>
      )}

      {/* Lista */}
      {carregando ? (
        <p style={{ color: 'var(--gray-500)' }}>Carregando…</p>
      ) : empresas.length === 0 ? (
        <div style={{
          background: '#fff', border: '1px dashed var(--gray-200)', borderRadius: 10,
          padding: 40, textAlign: 'center', color: 'var(--gray-500)',
        }}>
          <Building2 size={36} style={{ opacity: 0.4 }} />
          <p>Nenhuma empresa cadastrada ainda. Comece pelo botão "Nova empresa".</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {empresas.map((emp) => {
            const info = STATUS_INFO[emp.status] || STATUS_INFO.prospect;
            return (
              <div key={emp.id} style={{
                background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 10,
                padding: '14px 18px',
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 220 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <strong style={{ fontSize: '1rem' }}>{emp.nome_fantasia || emp.razao_social}</strong>
                      <span style={{
                        background: `${info.cor}18`, color: info.cor, fontSize: '0.75rem',
                        fontWeight: 700, padding: '2px 10px', borderRadius: 999,
                      }}>
                        {info.label}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.82rem', color: 'var(--gray-500)', marginTop: 2 }}>
                      {emp.razao_social} · CNPJ {formatarCnpj(emp.cnpj)}
                    </div>
                    {(emp.contato_nome || emp.contato_email) && (
                      <div style={{ fontSize: '0.82rem', color: 'var(--gray-500)' }}>
                        Contato: {[emp.contato_nome, emp.contato_email, emp.telefone].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => { setErroModal(null); setCopiado(false); setModalAdmin({ empresa: emp, nome: '', email: '', criado: null }); }}
                      title="Criar acesso de admin da empresa"
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6, background: '#fff',
                        color: 'var(--brand, #20a490)', border: '1px solid var(--brand, #20a490)', borderRadius: 8,
                        padding: '7px 12px', cursor: 'pointer', fontWeight: 600, fontSize: '0.83rem',
                      }}
                    >
                      <UserPlus size={15} /> Criar acesso
                    </button>
                    <button
                      onClick={() => {
                        setErroModal(null);
                        setModalEmpresa({
                          modo: 'editar', id: emp.id,
                          dados: {
                            cnpj: emp.cnpj, razao_social: emp.razao_social || '',
                            nome_fantasia: emp.nome_fantasia || '', contato_nome: emp.contato_nome || '',
                            contato_email: emp.contato_email || '', telefone: emp.telefone || '',
                            status: emp.status, observacoes: emp.observacoes || '',
                          },
                        });
                      }}
                      title="Editar empresa"
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6, background: '#fff',
                        color: 'var(--gray-500)', border: '1px solid var(--gray-200)', borderRadius: 8,
                        padding: '7px 12px', cursor: 'pointer', fontSize: '0.83rem',
                      }}
                    >
                      <Pencil size={15} /> Editar
                    </button>
                  </div>
                </div>
                {emp.admins?.length > 0 && (
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed var(--gray-200)' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase' }}>
                      Acessos criados
                    </span>
                    {emp.admins.map((a) => (
                      <div key={a.id} style={{ fontSize: '0.83rem', color: 'var(--gray-600)', display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                        <KeyRound size={13} color="#f39c12" /> {a.nome} · {a.email}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal empresa */}
      {modalEmpresa && (
        <div style={overlay}>
          <div style={modalBox}>
            <div style={modalHeader}>
              <strong>{modalEmpresa.modo === 'criar' ? 'Nova empresa' : 'Editar empresa'}</strong>
              <button onClick={() => setModalEmpresa(null)} style={btnFechar}><X size={18} /></button>
            </div>
            <Campo label="CNPJ *">
              <input
                style={{ ...inputStyle, background: modalEmpresa.modo === 'editar' ? 'var(--gray-100)' : '#fff' }}
                value={modalEmpresa.dados.cnpj}
                disabled={modalEmpresa.modo === 'editar'}
                placeholder="00.000.000/0000-00"
                onChange={(e) => setModalEmpresa((m) => ({ ...m, dados: { ...m.dados, cnpj: e.target.value } }))}
              />
            </Campo>
            <Campo label="Razão social *">
              <input style={inputStyle} value={modalEmpresa.dados.razao_social}
                onChange={(e) => setModalEmpresa((m) => ({ ...m, dados: { ...m.dados, razao_social: e.target.value } }))} />
            </Campo>
            <Campo label="Nome fantasia">
              <input style={inputStyle} value={modalEmpresa.dados.nome_fantasia}
                onChange={(e) => setModalEmpresa((m) => ({ ...m, dados: { ...m.dados, nome_fantasia: e.target.value } }))} />
            </Campo>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Campo label="Contato (nome)">
                <input style={inputStyle} value={modalEmpresa.dados.contato_nome}
                  onChange={(e) => setModalEmpresa((m) => ({ ...m, dados: { ...m.dados, contato_nome: e.target.value } }))} />
              </Campo>
              <Campo label="Telefone">
                <input style={inputStyle} value={modalEmpresa.dados.telefone}
                  onChange={(e) => setModalEmpresa((m) => ({ ...m, dados: { ...m.dados, telefone: e.target.value } }))} />
              </Campo>
            </div>
            <Campo label="E-mail do contato">
              <input style={inputStyle} type="email" value={modalEmpresa.dados.contato_email}
                onChange={(e) => setModalEmpresa((m) => ({ ...m, dados: { ...m.dados, contato_email: e.target.value } }))} />
            </Campo>
            <Campo label="Status">
              <select style={inputStyle} value={modalEmpresa.dados.status}
                onChange={(e) => setModalEmpresa((m) => ({ ...m, dados: { ...m.dados, status: e.target.value } }))}>
                {Object.entries(STATUS_INFO).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </Campo>
            <Campo label="Observações">
              <textarea style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} value={modalEmpresa.dados.observacoes}
                onChange={(e) => setModalEmpresa((m) => ({ ...m, dados: { ...m.dados, observacoes: e.target.value } }))} />
            </Campo>
            {erroModal && <div style={erroBox}>{erroModal}</div>}
            <button onClick={salvarEmpresa} disabled={salvando} style={btnPrimario}>
              {salvando ? 'Salvando…' : 'Salvar'}
            </button>
          </div>
        </div>
      )}

      {/* Modal criar acesso */}
      {modalAdmin && (
        <div style={overlay}>
          <div style={modalBox}>
            <div style={modalHeader}>
              <strong>Criar acesso: {modalAdmin.empresa.nome_fantasia || modalAdmin.empresa.razao_social}</strong>
              <button onClick={() => setModalAdmin(null)} style={btnFechar}><X size={18} /></button>
            </div>
            {!modalAdmin.criado ? (
              <>
                <p style={{ fontSize: '0.85rem', color: 'var(--gray-500)', marginTop: 0 }}>
                  Cria o administrador da empresa com senha provisória. Ele poderá
                  configurar marca, equipe e explorar o sistema por conta própria.
                </p>
                <Campo label="Nome completo *">
                  <input style={inputStyle} value={modalAdmin.nome}
                    onChange={(e) => setModalAdmin((m) => ({ ...m, nome: e.target.value }))} />
                </Campo>
                <Campo label="E-mail *">
                  <input style={inputStyle} type="email" value={modalAdmin.email}
                    onChange={(e) => setModalAdmin((m) => ({ ...m, email: e.target.value }))} />
                </Campo>
                {erroModal && <div style={erroBox}>{erroModal}</div>}
                <button onClick={criarAdmin} disabled={salvando || !modalAdmin.nome || !modalAdmin.email} style={btnPrimario}>
                  {salvando ? 'Criando…' : 'Criar acesso'}
                </button>
              </>
            ) : (
              <>
                <div style={{
                  background: '#eafaf1', border: '1px solid #27ae60', borderRadius: 8,
                  padding: 14, marginBottom: 12,
                }}>
                  <p style={{ margin: '0 0 8px', fontWeight: 700, color: '#27ae60' }}>Acesso criado</p>
                  <p style={{ margin: '4px 0', fontSize: '0.9rem' }}><strong>Usuário:</strong> {modalAdmin.criado.email}</p>
                  <p style={{ margin: '4px 0', fontSize: '0.9rem' }}>
                    <strong>Senha provisória:</strong>{' '}
                    <code style={{ background: '#fff', padding: '2px 8px', borderRadius: 4 }}>
                      {modalAdmin.criado.senha_provisoria}
                    </code>
                  </p>
                  <p style={{ margin: '8px 0 0', fontSize: '0.78rem', color: 'var(--gray-500)' }}>
                    Esta senha aparece só agora. Copie e repasse por canal privado.
                    Peça para trocarem no primeiro acesso.
                  </p>
                </div>
                <button onClick={copiarCredenciais} style={{ ...btnPrimario, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  <Copy size={15} /> {copiado ? 'Copiado!' : 'Copiar credenciais'}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const overlay = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
};
const modalBox = {
  background: '#fff', borderRadius: 12, padding: 20, width: '100%', maxWidth: 480,
  maxHeight: '90vh', overflowY: 'auto',
};
const modalHeader = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14,
};
const btnFechar = {
  background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)', padding: 4,
};
const btnPrimario = {
  width: '100%', background: 'var(--brand, #20a490)', color: 'var(--brand-ink, #fff)', border: 'none', borderRadius: 8,
  padding: '10px 16px', fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem',
};
const erroBox = {
  background: '#fdecea', color: '#e74c3c', padding: 10, borderRadius: 6,
  fontSize: '0.85rem', marginBottom: 12,
};

export default GestaoEmpresas;
